import AppBuilderAPI from 'dashboard/api/appBuilder';

const state = {
  records: [],
  metadata: { model_name: null, fields: [] },
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingMetadata: false,
  },
  meta: {
    count: 0,
  },
};

const getters = {
  getApps: $state => $state.records,
  getApp: $state => id => $state.records.find(a => Number(a.id) === Number(id)),
  getUIFlags: $state => $state.uiFlags,
  getMeta: $state => $state.meta,
  getMetadata: $state => $state.metadata,
};

const actions = {
  async fetchApps({ commit }, params = {}) {
    commit('setUIFlag', { isFetching: true });
    try {
      const response = await AppBuilderAPI.get(params);
      commit('setApps', response.data.payload);
      commit('setMeta', response.data.meta);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async fetchApp({ commit }, { id }) {
    commit('setUIFlag', { isFetchingItem: true });
    try {
      const response = await AppBuilderAPI.show(id);
      commit('addOrUpdateApp', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetchingItem: false });
    }
  },

  async createApp({ commit }, appData) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await AppBuilderAPI.create(appData);
      commit('addOrUpdateApp', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async updateApp({ commit }, { id, ...appData }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await AppBuilderAPI.update(id, appData);
      commit('addOrUpdateApp', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async deleteApp({ commit }, id) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await AppBuilderAPI.delete(id);
      commit('removeApp', id);
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  async fetchMetadata({ commit }, modelName = 'Ticket') {
    commit('setUIFlag', { isFetchingMetadata: true });
    try {
      const response = await AppBuilderAPI.fetchMetadata(modelName);
      commit('setMetadata', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetchingMetadata: false });
    }
  },

  // Pages -------------------------------------------------------------------
  async fetchPages(_, appId) {
    const response = await AppBuilderAPI.getPages(appId);
    return response.data.payload;
  },

  async createPage(_, { appId, ...page }) {
    const response = await AppBuilderAPI.createPage(appId, page);
    return response.data;
  },

  async updatePage(_, { appId, id, ...page }) {
    const response = await AppBuilderAPI.updatePage(appId, id, page);
    return response.data;
  },

  async deletePage(_, { appId, id }) {
    await AppBuilderAPI.deletePage(appId, id);
  },

  // Blocks ------------------------------------------------------------------
  async fetchBlocks(_, { appId, pageId }) {
    const response = await AppBuilderAPI.getBlocks(appId, pageId);
    return response.data.payload;
  },

  async createBlock(_, { appId, pageId, ...block }) {
    const response = await AppBuilderAPI.createBlock(appId, pageId, block);
    return response.data;
  },

  async updateBlock(_, { appId, pageId, id, ...block }) {
    const response = await AppBuilderAPI.updateBlock(appId, pageId, id, block);
    return response.data;
  },

  async deleteBlock(_, { appId, pageId, id }) {
    await AppBuilderAPI.deleteBlock(appId, pageId, id);
  },

  // Data sources ------------------------------------------------------------
  async fetchDataSources(_, appId) {
    const response = await AppBuilderAPI.getDataSources(appId);
    return response.data.payload;
  },

  async createDataSource(_, { appId, ...dataSource }) {
    const response = await AppBuilderAPI.createDataSource(appId, dataSource);
    return response.data;
  },

  async updateDataSource(_, { appId, id, ...dataSource }) {
    const response = await AppBuilderAPI.updateDataSource(
      appId,
      id,
      dataSource
    );
    return response.data;
  },

  async deleteDataSource(_, { appId, id }) {
    await AppBuilderAPI.deleteDataSource(appId, id);
  },

  // Members -----------------------------------------------------------------
  async fetchMembers(_, { appId, ...params }) {
    const response = await AppBuilderAPI.getMembers(appId, params);
    return response.data;
  },

  async createMember(_, { appId, ...member }) {
    const response = await AppBuilderAPI.createMember(appId, member);
    return response.data;
  },

  async updateMember(_, { appId, id, ...member }) {
    const response = await AppBuilderAPI.updateMember(appId, id, member);
    return response.data;
  },

  async deleteMember(_, { appId, id }) {
    await AppBuilderAPI.deleteMember(appId, id);
  },

  async resendMemberInvite(_, { appId, id }) {
    const response = await AppBuilderAPI.resendMemberInvite(appId, id);
    return response.data;
  },

  // Member groups -----------------------------------------------------------
  async fetchMemberGroups(_, appId) {
    const response = await AppBuilderAPI.getMemberGroups(appId);
    return response.data.payload;
  },

  async createMemberGroup(_, { appId, ...group }) {
    const response = await AppBuilderAPI.createMemberGroup(appId, group);
    return response.data;
  },

  async updateMemberGroup(_, { appId, id, ...group }) {
    const response = await AppBuilderAPI.updateMemberGroup(appId, id, group);
    return response.data;
  },

  async deleteMemberGroup(_, { appId, id }) {
    await AppBuilderAPI.deleteMemberGroup(appId, id);
  },
};

const mutations = {
  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
  setApps($state, apps) {
    $state.records = apps || [];
  },
  setMeta($state, meta = {}) {
    $state.meta = {
      count: meta.apps_count || meta.count || 0,
    };
  },
  setMetadata($state, metadata = {}) {
    $state.metadata = {
      model_name: metadata.model_name || null,
      fields: metadata.fields || [],
    };
  },
  addOrUpdateApp($state, app) {
    if (!app || !app.id) return;
    const index = $state.records.findIndex(a => a.id === app.id);
    if (index === -1) {
      $state.records = [app, ...$state.records];
    } else {
      const next = [...$state.records];
      next[index] = app;
      $state.records = next;
    }
  },
  removeApp($state, id) {
    $state.records = $state.records.filter(a => a.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
